import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'
import { formatMoney } from "@/lib/money"

interface AirwallexConfigurationCardProps {
    processor: ProcessorDetails
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getClientId = (options: Record<string, unknown> | null) => {
    return options?.clientId as string | undefined
}

export const AirwallexConfigurationCard = ({ processor }: AirwallexConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const clientId = getClientId(processor.options)

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('airwallexConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('clientId')}</p>
                        <p className="font-mono text-sm">{clientId || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('clientApiKey')}</p>
                        <p className="font-mono text-sm">••••••••••••••••</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('webhookSecret')}</p>
                        <p className="font-mono text-sm">••••••••••••••••</p>
                    </div>

                    {processor.baseCurrency && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency}</p>
                        </div>
                    )}

                    {processor.monthlyProcessingCapacity !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('monthlyCapacity')}</p>
                            <p className="font-semibold">
                                {formatMoney(processor.monthlyProcessingCapacity, processor.baseCurrency || 'AUD')}
                            </p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                        <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                    </div>

                    {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
                        <div className="col-span-full">
                            <p className="text-sm text-muted-foreground mb-2">{t('supportedCurrencies')}</p>
                            <div className="flex flex-wrap gap-2">
                                {processor.supportedCurrencies.map((currency) => (
                                    <Badge key={currency} variant="outline">
                                        {currency}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}

